import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { readSavedWheels } from '../utils/savedWheels';
import type { SavedWheel } from '../types/wheel';

/**
 * Returning-user banner — "Welcome back, [wheel] is saved."
 *
 * Shown on the homepage when the user has at least one saved wheel in
 * localStorage. The most recently *updated* wheel is surfaced (falling back
 * to savedAt for wheels that predate the updatedAt field).
 *
 * Dismissing hides for the session (sessionStorage) rather than permanently,
 * because the banner is genuinely useful when a teacher opens the site on
 * Monday morning and has forgotten where they left off.
 */

const DISMISS_KEY = 'npw_resume_dismissed';

interface ResumeWheelBannerProps {
  /** Called when the user clicks "Load it" — parent loads the wheel. */
  onLoad: (wheel: SavedWheel) => void;
  /** Called when the user has multiple wheels and clicks "View saved wheels" */
  onOpenSaved: () => void;
}

export default function ResumeWheelBanner({ onLoad, onOpenSaved }: ResumeWheelBannerProps) {
  const [wheel, setWheel] = useState<SavedWheel | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [dismissed, setDismissed] = useState(true); // hidden until checked

  useEffect(() => {
    // Don't render during SSR / prerender
    if (typeof window === 'undefined') return;

    // Already dismissed this session
    try { if (sessionStorage.getItem(DISMISS_KEY)) return; } catch { /* blocked */ }

    const wheels = readSavedWheels();
    if (wheels.length === 0) return;

    // Sort by most recently worked on: updatedAt ?? savedAt
    const sorted = [...wheels].sort((a, b) => {
      const ta = new Date(a.updatedAt ?? a.savedAt).getTime();
      const tb = new Date(b.updatedAt ?? b.savedAt).getTime();
      return tb - ta;
    });

    setWheel(sorted[0]);
    setTotalCount(wheels.length);
    setDismissed(false);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* blocked */ }
  };

  if (dismissed || !wheel) return null;

  return (
    <div
      role="status"
      className="bg-violet-50 dark:bg-violet-900/15 border-b border-violet-100 dark:border-violet-800/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
        <p className="text-sm text-gray-700 dark:text-gray-200 min-w-0">
          {totalCount === 1 ? (
            <>
              Welcome back &mdash;{' '}
              <strong className="font-semibold text-gray-900 dark:text-white">{wheel.name}</strong>
              {' '}is saved with {wheel.entryCount ?? wheel.entries.length} entries.{' '}
              <button
                type="button"
                onClick={() => { onLoad(wheel); dismiss(); }}
                className="font-semibold text-violet-600 dark:text-violet-400 hover:underline"
              >
                Load it &rarr;
              </button>
            </>
          ) : (
            <>
              You have{' '}
              <strong className="font-semibold text-gray-900 dark:text-white">{totalCount} saved wheels</strong>.{' '}
              <button
                type="button"
                onClick={() => { onOpenSaved(); dismiss(); }}
                className="font-semibold text-violet-600 dark:text-violet-400 hover:underline"
              >
                View saved wheels &rarr;
              </button>
            </>
          )}
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

